import type { WorkflowRunSummary } from '../../types/backend';
import { safeGetTimestampAge } from '../../shared/utils/dateUtils';

/**
 * One status vocabulary for every surface that shows a run.
 *
 * The panel, the procedure bar and the run view all answer the same question, so a word that
 * changes in one of them must change in all three. Kept out of any component for that reason.
 */
export const STATUS_CHIP: Record<
  string,
  { label: string; color: 'default' | 'info' | 'warning' | 'success' | 'error' }
> = {
  running: { label: 'Running', color: 'info' },
  awaiting_gate: { label: 'Waiting on you', color: 'warning' },
  // A blocked run moves only when someone retries the failed step or abandons the run.
  blocked: { label: 'Needs you', color: 'warning' },
  completed: { label: 'Done', color: 'success' },
  abandoned: { label: 'Stopped', color: 'error' },
};

const NEEDS_ACCESS = { label: 'Needs access', color: 'warning' as const };

// A run that says `running` with nothing behind it is not running, and a word of its own is the
// only way the lists stop presenting it as work in progress. "Stranded" is taken: it names a run
// whose pinned workflow version was deleted.
const NO_AGENT = { label: 'No agent', color: 'error' as const };

/** A run held by the operator's own access asks for something different from a failed step. */
export function statusChip(run: WorkflowRunSummary) {
  if (hasNoSessionBehindIt(run)) return NO_AGENT;
  if (run.status === 'blocked' && run.failed_cause === 'access') return NEEDS_ACCESS;
  return STATUS_CHIP[run.status] ?? { label: run.status, color: 'default' as const };
}

/**
 * Whether a run can still be acted on. Blocked counts: it is the state that most needs a person,
 * and treating it as finished would make reporting a step failed a way out of the procedure.
 *
 * Here rather than in a component because three surfaces decide whether to offer a way out, and
 * two of them disagreeing about what "still going" means is how one of them starts offering an
 * abandon on a run that already ended.
 */
export function isRunActive(status: string): boolean {
  return status !== 'completed' && status !== 'abandoned';
}

/**
 * How long a run can report `running` before no agent session can possibly be behind it.
 *
 * AgentCore caps a session at four hours (`maxLifetime`, `cdk/lib/opspilot-agentcore-stack.ts`),
 * and the extra hour is margin for a session that began just before the cap. Nothing links the
 * two numbers: raise the cap there and this has to be raised here by hand, or the screen starts
 * calling live runs dead.
 *
 * Every shorter deadline (a step budget, a heartbeat) is a judgement about what is too slow, and
 * a wrong judgement writes off work still in flight. This one needs no such judgement, but it is
 * read against the viewer's own clock rather than the server's, so a badly skewed machine can
 * still be wrong in both directions. That is why this marks a run for a person to look at and
 * never ends one: ending it is WF-UC-15, and belongs where the clock is the server's.
 */
export const NO_SESSION_AFTER_MS = 5 * 60 * 60 * 1000;

/**
 * Whether nothing can still be walking this run, judged from the viewer's clock.
 *
 * Only a `running` run qualifies. A run awaiting a gate or blocked on a step is a person's
 * decision that has not been made, however long it has waited, and calling that stranded would
 * tell the operator to end the very runs that are waiting for them (WF-UC-15 extension 2b).
 */
export function hasNoSessionBehindIt(run: WorkflowRunSummary): boolean {
  if (run.status !== 'running') return false;
  const age = safeGetTimestampAge(run.updated_at);
  return age !== null && age > NO_SESSION_AFTER_MS;
}
